import type {
  AnyProtocolMessage,
  PeerLeaveReason,
  PeerRole,
  ProtocolMessage,
  ProtocolPayloads,
} from '@screenshare/protocol';

export type PeerSignalType =
  'peer.offer' | 'peer.answer' | 'peer.ice' | 'peer.ready' | 'peer.leave';

export type LocalMediaSource = 'video' | 'screen-audio' | 'microphone';

export type PeerSignalMessage = Extract<AnyProtocolMessage, { readonly type: PeerSignalType }>;

export interface PeerSignalingChannel {
  send<Type extends PeerSignalType>(type: Type, payload: ProtocolPayloads[Type]): void;
  subscribe(listener: (message: PeerSignalMessage) => void): () => void;
}

export type WebRtcLifecycleState =
  | 'idle'
  | 'starting'
  | 'negotiating'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'failed'
  | 'closed';

export type WebRtcErrorCode =
  | 'invalid-state'
  | 'invalid-track'
  | 'negotiation-failed'
  | 'remote-description-failed'
  | 'ice-candidate-failed'
  | 'signaling-failed';

export class WebRtcError extends Error {
  constructor(
    readonly code: WebRtcErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'WebRtcError';
  }
}

export interface LocalMediaTracks {
  readonly video?: MediaStreamTrack;
  readonly screenAudio?: MediaStreamTrack;
  readonly microphone?: MediaStreamTrack;
  readonly screenStream?: MediaStream;
  readonly microphoneStream?: MediaStream;
}

export interface WebRtcEvents {
  readonly statechange: {
    readonly previous: WebRtcLifecycleState;
    readonly current: WebRtcLifecycleState;
    readonly cause: string;
  };
  readonly connectionstatechange: { readonly state: RTCPeerConnectionState };
  readonly iceconnectionstatechange: { readonly state: RTCIceConnectionState };
  readonly icegatheringstatechange: { readonly state: RTCIceGatheringState };
  readonly signalingstatechange: { readonly state: RTCSignalingState };
  readonly remotetrack: {
    readonly track: MediaStreamTrack;
    readonly streams: readonly MediaStream[];
    readonly transceiver: RTCRtpTransceiver;
  };
  readonly peerready: { readonly role: PeerRole };
  readonly peerreset: { readonly role: PeerRole };
  readonly peerleft: { readonly reason: PeerLeaveReason };
  readonly error: { readonly error: WebRtcError; readonly fatal: boolean };
}

export interface IceRestartOptions {
  readonly forceRelay?: boolean;
}

export interface IceRestartResult {
  readonly relayForced: boolean;
}

export type PeerOfferMessage = ProtocolMessage<'peer.offer'>;
export type PeerAnswerMessage = ProtocolMessage<'peer.answer'>;
export type PeerIceMessage = ProtocolMessage<'peer.ice'>;

export type PeerConnectionFactory = (configuration: RTCConfiguration) => RTCPeerConnection;
